import { useTranslation } from "react-i18next";
import { useFaturas } from "@/hooks/useFaturas";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Download, AlertTriangle, Sparkles, Upload, RefreshCw, Layers, Check, Search, X } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { AIBatchImportDrawer } from "./AIBatchImportDrawer";

const _localMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const FaturasTab = () => {
  const { t } = useTranslation();
  const {
    bills, suppliers, categories, loading,
    month, setMonth,
    viewAll, toggleViewAll,
    isDateSuspicious,
    billsCount,
    showNew, setShowNew, editBill,
    filter, setFilter,
    search, setSearch,
    showDetail, setShowDetail,
    form, setForm, items, calcTotal,
    filteredBills, totalPending, totalPaid, totalAll,
    overdueBills, overdueTotal,
    getDaysOverdue,
    addItem, removeItem, updateItem,
    openEdit, resetForm, handleSubmit,
    markPaid, deleteBill, selectSupplier,
    exportCSV, exportExcel,
    aiLoading, aiResult, setAiResult,
    handleAiUpload, applyAiResult, createFromAi, aiCreating,
    fetchData, runDebug,
  } = useFaturas();

  const fileInputRef = useRef(null);
  const [updateCosts, setUpdateCosts] = useState(true);
  const [showBatch, setShowBatch] = useState(false);
  // Reset date confirmation whenever the AI result changes
  const [dateConfirmed, setDateConfirmed] = useState(false);
  useEffect(() => { setDateConfirmed(false); }, [aiResult]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4" data-testid="faturas-tab">
      {/* Ver tudo — banner */}
      {viewAll && (
        <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-sm text-amber-800 font-semibold flex items-center gap-2" data-testid="view-all-banner">
          <span>★ Mostrando todas as faturas (todos os meses)</span>
          <button className="ml-auto text-xs underline font-normal" onClick={toggleViewAll}>Voltar ao mês selecionado</button>
        </div>
      )}
      {/* Summary Cards — DRE usa apenas faturas PAGAS; esta aba mostra TODAS (pagas + pendentes) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Total Faturas</p>
          <span className="text-2xl font-black">{billsCount}</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Total Geral</p>
          <span className="text-2xl font-black">{totalAll.toFixed(2)}€</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Pendente</p>
          <span className="text-2xl font-black text-amber-600">{totalPending.toFixed(2)}€</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Pago</p>
          <span className="text-2xl font-black text-emerald-600">{totalPaid.toFixed(2)}€</span>
        </CardContent></Card>
      </div>

      {/* Overdue Alert */}
      {overdueBills.length > 0 && (
        <Card className="bg-red-50 border-red-200" data-testid="overdue-alert">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-red-800 text-sm">{overdueBills.length} fatura{overdueBills.length > 1 ? "s" : ""} vencida{overdueBills.length > 1 ? "s" : ""} - Total: {overdueTotal.toFixed(2)}€</p>
              <div className="mt-1 space-y-0.5">
                {overdueBills.slice(0, 3).map(b => (
                  <p key={b.id} className="text-xs text-red-700">{b.supplier_name} - {b.total?.toFixed(2)}€ (vencida há {getDaysOverdue(b.due_date)} dias)</p>
                ))}
                {overdueBills.length > 3 && <p className="text-xs text-red-600 font-medium">+{overdueBills.length - 3} mais...</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value || _localMonth())}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="month-picker"
              title={viewAll ? "Selecionar mês (sai do modo Ver tudo)" : "Mês selecionado"}
            />
            <Button
              variant={viewAll ? "default" : "outline"}
              size="sm"
              className="text-xs h-8"
              onClick={toggleViewAll}
              data-testid="all-months-btn"
              title={viewAll ? "Voltar ao mês selecionado" : "Ver faturas de todos os meses"}
            >
              {viewAll ? "Ver mês" : "Ver tudo"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 border-orange-400 text-orange-700 hover:bg-orange-50"
              onClick={runDebug}
              title="Diagnóstico: mostra restaurant_id e últimas faturas no banco"
            >
              🔍 Debug BD
            </Button>
          </div>
          <div className="flex gap-1">
            {[{ k: "all", l: "Todas" }, { k: "pending", l: "Pendentes" }, { k: "paid", l: "Pagas" }].map(f => (
              <Button key={f.k} variant={filter === f.k ? "default" : "outline"} size="sm" className="text-xs" onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}>{f.l}</Button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("financiero.searchBills")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-8 h-8 text-xs w-full sm:w-64"
              data-testid="bill-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {search && (
            <span className="text-xs text-muted-foreground">{filteredBills.length} resultado{filteredBills.length !== 1 ? "s" : ""}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportCSV} data-testid="export-csv-btn"><Download className="w-3 h-3" />CSV</Button>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportExcel} data-testid="export-excel-btn"><Download className="w-3 h-3" />Excel</Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs border-violet-400 text-violet-700 hover:bg-violet-50"
            onClick={() => setShowBatch(true)}
            data-testid="batch-import-btn"
          >
            <Layers className="w-3 h-3" />Lote IA
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-violet-400 text-violet-700 hover:bg-violet-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={aiLoading}
            data-testid="ai-import-btn"
          >
            {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {aiLoading ? "A analisar..." : "Importar com IA"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={e => { if (e.target.files?.[0]) handleAiUpload(e.target.files[0]); e.target.value = ""; }}
          />
          <Button className="gap-2" onClick={() => { resetForm(); setShowNew(true); }} data-testid="new-bill-btn"><Plus className="w-4 h-4" />Lançar Fatura</Button>
        </div>
      </div>

      {/* Bills — Mobile cards */}
      <div className="sm:hidden divide-y divide-border/30 rounded-lg border border-border/50 bg-card">
        {filteredBills.map(b => {
          const total = (b.declared_total > 0 ? b.declared_total : b.total)?.toFixed(2);
          return (
            <div key={b.id} className="p-3 space-y-2 cursor-pointer" data-testid={`bill-row-${b.id?.slice(-6)}`} onClick={() => setShowDetail(b)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-medium text-sm block truncate">{b.supplier_name || "-"}</span>
                  {b.invoice_number && <span className="text-xs text-muted-foreground">#{b.invoice_number}</span>}
                </div>
                <span className="font-bold text-sm shrink-0">{total}€</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {b.status === "paid" ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Pago</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>}
                <span className="text-xs text-muted-foreground">{b.date}</span>
                {b.category && <span className="text-xs text-muted-foreground">{categories[b.category] || b.category}</span>}
              </div>
              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                {b.status === "pending" && <Button variant="ghost" size="sm" className="text-emerald-600 text-xs h-7" onClick={() => markPaid(b.id)} data-testid={`pay-bill-${b.id?.slice(-6)}`}>Pagar</Button>}
                <Button variant="ghost" size="sm" className="text-blue-500 text-xs h-7" onClick={() => openEdit(b)} data-testid={`edit-bill-${b.id?.slice(-6)}`}>Editar</Button>
                <Button variant="ghost" size="sm" className="text-red-400 text-xs h-7" onClick={() => deleteBill(b.id)} data-testid={`delete-bill-${b.id?.slice(-6)}`}>×</Button>
              </div>
            </div>
          );
        })}
        {filteredBills.length === 0 && <p className="p-8 text-center text-muted-foreground">Nenhuma fatura encontrada</p>}
      </div>

      {/* Bills — Desktop table */}
      <div className="hidden sm:block bg-card rounded-lg border border-border/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border/50 text-muted-foreground text-xs uppercase">
            <th className="text-left p-3 font-medium">Fornecedor</th>
            <th className="text-left p-3 font-medium hidden md:table-cell">Categoria</th>
            <th className="text-left p-3 font-medium">Data</th>
            <th className="text-right p-3 font-medium">Total</th>
            <th className="text-center p-3 font-medium">Status</th>
            <th className="p-3 w-28"></th>
          </tr></thead>
          <tbody className="divide-y divide-border/30">
            {filteredBills.map(b => (
              <tr key={b.id} className="hover:bg-muted/50 cursor-pointer" data-testid={`bill-row-${b.id?.slice(-6)}`} onClick={() => setShowDetail(b)}>
                <td className="p-3"><span className="font-medium">{b.supplier_name || "-"}</span>{b.invoice_number && <span className="text-xs text-muted-foreground ml-1">#{b.invoice_number}</span>}</td>
                <td className="p-3 text-muted-foreground hidden md:table-cell">{categories[b.category] || b.category}</td>
                <td className="p-3 text-muted-foreground">{b.date}</td>
                <td className="p-3 text-right font-bold">{(b.declared_total > 0 ? b.declared_total : b.total)?.toFixed(2)}€</td>
                <td className="p-3 text-center">
                  {b.status === "paid" ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Pago</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>}
                </td>
                <td className="p-3" onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    {b.status === "pending" && <Button variant="ghost" size="sm" className="text-emerald-600 text-xs h-7" onClick={() => markPaid(b.id)} data-testid={`pay-bill-${b.id?.slice(-6)}`}>Pagar</Button>}
                    <Button variant="ghost" size="sm" className="text-blue-500 text-xs h-7" onClick={() => openEdit(b)} data-testid={`edit-bill-${b.id?.slice(-6)}`}>Editar</Button>
                    <Button variant="ghost" size="sm" className="text-red-400 text-xs h-7" onClick={() => deleteBill(b.id)} data-testid={`delete-bill-${b.id?.slice(-6)}`}>×</Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredBills.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nenhuma fatura encontrada</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Bill Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={(open) => !open && setShowDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="uppercase font-bold text-sm">Detalhes da Fatura</DialogTitle></DialogHeader>
          {showDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Fornecedor</span><p className="font-medium">{showDetail.supplier_name || "-"}</p></div>
                <div><span className="text-muted-foreground text-xs">Nº Factura</span><p className="font-medium">{showDetail.invoice_number || "-"}</p></div>
                <div><span className="text-muted-foreground text-xs">Categoria</span><p className="font-medium">{categories[showDetail.category] || showDetail.category}</p></div>
                <div><span className="text-muted-foreground text-xs">Status</span><p>{showDetail.status === "paid" ? <Badge className="bg-emerald-100 text-emerald-700">Pago</Badge> : <Badge className="bg-amber-100 text-amber-700">Pendente</Badge>}</p></div>
                <div><span className="text-muted-foreground text-xs">Data</span><p className="font-medium">{showDetail.date}</p></div>
                <div><span className="text-muted-foreground text-xs">Vencimento</span><p className="font-medium">{showDetail.due_date || "-"}</p></div>
              </div>
              {showDetail.items?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Itens</p>
                  <div className="bg-muted/50 rounded-lg divide-y divide-border/30">
                    {showDetail.items.map((it, i) => (
                      <div key={i} className="flex justify-between px-3 py-2 text-sm">
                        <span>{it.name || `Item ${i + 1}`}</span>
                        <span className="font-medium">{it.quantity}x {it.price?.toFixed(2)}€ = {(it.quantity * it.price).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="pt-2 border-t space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Base s/ IVA</span>
                  <span>{showDetail.total?.toFixed(2)}€</span>
                </div>
                {showDetail.declared_total > 0 && Math.abs(showDetail.declared_total - showDetail.total) > 0.01 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>IVA</span>
                    <span>{(showDetail.declared_total - showDetail.total).toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center font-bold text-lg pt-0.5">
                  <span>Total Final</span>
                  <span>{(showDetail.declared_total > 0 ? showDetail.declared_total : showDetail.total)?.toFixed(2)}€</span>
                </div>
              </div>
              {showDetail.notes && <p className="text-sm text-muted-foreground italic">{showDetail.notes}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(null)}>{t("common.close")}</Button>
            <Button onClick={() => { openEdit(showDetail); setShowDetail(null); }}>{t("common.edit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Review Dialog */}
      <Dialog open={!!aiResult} onOpenChange={(open) => !open && setAiResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Sparkles className="w-5 h-5" />
              Fatura Extraída pela IA
            </DialogTitle>
          </DialogHeader>
          {aiResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Fornecedor</span><p className="font-bold">{aiResult.supplier_name || "—"}</p></div>
                <div><span className="text-muted-foreground text-xs">Nº Fatura</span><p className="font-medium">{aiResult.invoice_number || "—"}</p></div>
                <div><span className="text-muted-foreground text-xs">Data</span><p className="font-medium">{aiResult.date || "—"}</p></div>
                <div><span className="text-muted-foreground text-xs">Categoria</span><p className="font-medium">{categories[aiResult.category] || aiResult.category}</p></div>
              </div>

              {aiResult.items?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Itens Detetados</p>
                  <div className="bg-muted/40 rounded-lg divide-y divide-border/30">
                    {aiResult.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                        <div className="flex-1">
                          <span className="font-medium">{it.name}</span>
                          {it.stock_id && (
                            <span className="ml-2 text-[10px] bg-violet-100 text-violet-700 rounded px-1 py-0.5">✓ Ingrediente</span>
                          )}
                        </div>
                        <span className="text-muted-foreground text-xs">{it.quantity} {it.unit}</span>
                        <span className="font-bold">{it.price?.toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const costItems = (aiResult.items || []).filter(it => it.stock_id && it.new_cost > 0);
                if (!costItems.length) return null;
                return (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-bold text-violet-800 uppercase">Atualização de Custos (Fichas Técnicas)</p>
                    <p className="text-xs text-violet-700">A IA encontrou preços para estes ingredientes. Deseja atualizar os custos?</p>
                    <div className="space-y-1">
                      {costItems.map((it, i) => (
                        <div key={i} className="flex justify-between text-xs text-violet-800">
                          <span>{it.matched_stock_name || it.name}</span>
                          <span>
                            <span className="line-through text-violet-400">{it.current_cost?.toFixed(4)}€/un</span>
                            {" → "}
                            <span className="font-bold">{it.new_cost?.toFixed(4)}€/un</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-violet-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updateCosts}
                        onChange={e => setUpdateCosts(e.target.checked)}
                        className="w-3.5 h-3.5 accent-violet-600"
                      />
                      Atualizar custos nas fichas técnicas
                    </label>
                  </div>
                );
              })()}

              <div className="flex justify-between items-center pt-2 border-t">
                <div className="text-sm text-muted-foreground">
                  {aiResult.tax_amount > 0 && <span>IVA {aiResult.tax_rate}%: {aiResult.tax_amount?.toFixed(2)}€ · </span>}
                  <span className="font-black text-lg text-foreground">Total: {aiResult.total?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Date warning — shown when AI date is >12 months away from selected month */}
              {(() => {
                const suspicious = isDateSuspicious(aiResult.date);
                if (!suspicious || dateConfirmed) return null;
                return (
                  <div className="bg-amber-50 border border-amber-400 rounded-lg p-3 space-y-2" data-testid="date-warning">
                    <p className="font-bold text-amber-900 text-sm">⚠️ Data suspeita detectada</p>
                    <p className="text-amber-800 text-xs">
                      A IA identificou a data <strong>{aiResult.date}</strong>, que está fora do mês selecionado (<strong>{month}</strong>).
                      Verifique se é a data correta antes de criar.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={() => applyAiResult(aiResult, updateCosts)}>
                        Editar e corrigir data
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs text-amber-700"
                        onClick={() => setDateConfirmed(true)}>
                        Confirmar data {aiResult.date}
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAiResult(null)} className="sm:mr-auto">Cancelar</Button>
            <Button
              variant="outline"
              className="gap-2 text-violet-700 border-violet-300 hover:bg-violet-50"
              onClick={() => applyAiResult(aiResult, updateCosts)}
              disabled={aiCreating}
            >
              <Upload className="w-4 h-4" />
              Editar antes de criar
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold"
              onClick={() => createFromAi(aiResult, updateCosts)}
              disabled={aiCreating || (isDateSuspicious(aiResult?.date) && !dateConfirmed)}
              data-testid="create-from-ai-btn"
              title={isDateSuspicious(aiResult?.date) && !dateConfirmed ? "Confirme ou corrija a data antes de criar" : undefined}
            >
              {aiCreating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {aiCreating ? "A criar..." : "CRIAR FATURA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New/Edit Bill Dialog */}
      <Dialog open={showNew} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="uppercase font-bold">{editBill ? t("common.edit") : t("financiero.createBill")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("financiero.supplier")}</Label>
                <Select value={form.supplier_id} onValueChange={selectSupplier}>
                  <SelectTrigger data-testid="bill-supplier-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Ou escreva nome novo" value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} className="text-xs" data-testid="bill-supplier-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.category")}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="bill-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Nº Factura</Label><Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="bill-invoice-num" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="bill-date" /></div>
              <div className="space-y-2"><Label>Vencimento</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="bill-due-date" /></div>
            </div>
            <div>
              <Label className="mb-2 block">Itens da Nota</Label>
              <div className="space-y-2">
                {items.map((item, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Produto" value={item.name} onChange={e => updateItem(i, "name", e.target.value)} className="flex-1" data-testid={`bill-item-name-${i}`} />
                    <Input type="number" placeholder="Qtd" value={item.quantity} onChange={e => updateItem(i, "quantity", parseFloat(e.target.value) || 0)} className="w-20" data-testid={`bill-item-qty-${i}`} />
                    <Input type="number" placeholder="Preço" value={item.price} onChange={e => updateItem(i, "price", parseFloat(e.target.value) || 0)} className="w-24" step="0.01" data-testid={`bill-item-price-${i}`} />
                    <span className="text-xs font-bold w-16 text-right">{(item.quantity * item.price).toFixed(2)}€</span>
                    {items.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeItem(i)}>×</Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="text-xs" onClick={addItem} data-testid="add-bill-item"><Plus className="w-3 h-3 mr-1" />Adicionar Item</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Total Calculado</Label><div className="text-2xl font-black" data-testid="bill-calc-total">{calcTotal.toFixed(2)}€</div></div>
              <div className="space-y-2"><Label>Total da Nota (conferir)</Label><Input type="number" step="0.01" placeholder={calcTotal.toFixed(2)} value={form.declared_total} onChange={e => setForm({ ...form, declared_total: e.target.value })} data-testid="bill-declared-total" /></div>
            </div>
            <Input placeholder={t("financiero.notes")} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="bill-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} data-testid="submit-bill-btn">{editBill ? t("common.update") : t("financiero.createBill")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIBatchImportDrawer
        open={showBatch}
        onClose={() => setShowBatch(false)}
        categories={categories}
        onSuccess={fetchData}
        month={month}
      />
    </div>
  );
};
